/**
 * Whether the book is still taking a catalogue outcome — `.m-outcome-status` in
 * `shared/modals.css` (CAE-1574).
 *
 * In the kit because `modals.css` draws it once and two screens mount it: the
 * Contest Overview's Reels section, and Edit › Reels, whose own spec
 * (`06-edit-reels/README.md`) names `<m-outcome-status>` in its column table.
 * That shared indicator — not the row around it — is what the two screens
 * actually have in common; their rows differ in their columns, their odds
 * layout and everything they let an operator do.
 *
 * Named for the **catalogue outcome** rather than the Reel Option that shows it,
 * which keeps CONTEXT.md's "avoid *outcome* for a Reel Option" intact: the state
 * being drawn is the feed's `active` flag on its own outcome, and a Reel Option
 * inherits it rather than owning it.
 *
 * > The screen README claims the active dot pulses. `.m-outcome-status-active
 * > .m-outcome-status-dot` carries no animation — only `.m-pill-live .m-pill-dot`
 * > does — and the standing rule since CAE-1537 is that the stylesheet wins.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';

export interface OutcomeStatusProps {
  /**
   * `null` when the catalogue holds no outcome for this selection at all. That
   * is the absence of a claim, not a live one, so it draws an em-dash rather
   * than borrowing Active's appearance — §7's "no stats slop" rule, the same
   * one the health strip and `FixtureRow`'s market count follow.
   */
  suspended: boolean | null;
}

export const OutcomeStatus = ({ suspended }: OutcomeStatusProps) => {
  if (suspended === null) {
    return (
      <Box component="span" sx={(theme: Theme) => ({ color: theme.tokens.color.ink4 })}>
        —
      </Box>
    );
  }

  return (
    <Box
      component="span"
      sx={(theme: Theme) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '12px',
        fontWeight: 500,
        color: suspended ? theme.tokens.color.warn : theme.tokens.componentColor.marketBadgeOkText,
      })}
    >
      <Box
        aria-hidden
        component="span"
        sx={(theme: Theme) => ({
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          backgroundColor: suspended ? theme.tokens.color.warn : theme.tokens.color.success,
        })}
      />
      {suspended ? 'Suspended' : 'Active'}
    </Box>
  );
};
